import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { salesAPI, invoicesAPI } from '../services/api';
import { ArrowLeft, Printer, Mail, Copy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SaleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sale, setSale] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const invoiceRef = useRef();

  useEffect(() => {
    fetchSale();
  }, [id]);

  const fetchSale = async () => {
    try {
      const response = await salesAPI.getOne(id);
      setSale(response.data);

      // Fetch associated invoice if it exists
      if (response.data.invoice_number) {
        try {
          const invoicesResponse = await invoicesAPI.getAll();
          const matchingInvoice = invoicesResponse.data.find(
            inv => inv.invoice_number === response.data.invoice_number
          );
          if (matchingInvoice) {
            setInvoice(matchingInvoice);
          }
        } catch (invErr) {
          console.error('Error fetching invoice:', invErr);
        }
      }
    } catch (err) {
      console.error('Error fetching sale:', err);
      setError('Sale not found');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopyInvoiceNumber = () => {
    navigator.clipboard.writeText(sale.invoice_number);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailInvoice = () => {
    const subject = `Invoice ${sale.invoice_number} - ${sale.watch_brand} ${sale.watch_model}`;
    const body = `Dear ${sale.client_name || 'Customer'},\n\nPlease find attached your invoice for the purchase of ${sale.watch_brand} ${sale.watch_model}.\n\nInvoice Number: ${sale.invoice_number}\nAmount: $${(Number(sale.sale_price) || 0).toLocaleString()}\n\nThank you for your business!\n\nBest regards,\n${user?.business_name || user?.name}`;
    window.location.href = `mailto:${sale.client_email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !sale) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Sale</h3>
          <p className="text-gray-600 mb-6">{error || 'Sale not found'}</p>
          <button
            onClick={() => navigate('/sales')}
            className="px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all"
          >
            Back to Sales
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Bar - Hidden when printing */}
      <div className="flex items-center justify-between print:hidden">
        <button
          onClick={() => navigate('/sales')}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Sales
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCopyInvoiceNumber}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
            title="Copy invoice number"
          >
            <Copy className="w-4 h-4" />
            {copied ? 'Copied!' : 'Copy #'}
          </button>
          <button
            onClick={handleEmailInvoice}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl font-medium hover:bg-blue-100 transition-all"
            title="Email invoice"
          >
            <Mail className="w-4 h-4" />
            Email
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all"
          >
            <Printer className="w-5 h-5" />
            Print Invoice
          </button>
        </div>
      </div>

      {/* Invoice */}
      <div
        ref={invoiceRef}
        className="bg-white rounded-3xl shadow-xl p-12 border-2 border-gray-100 print:shadow-none print:border-0"
      >
        {/* Invoice Header */}
        <div className="mb-12">
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-2">INVOICE</h1>
              <p className="text-gray-500 text-lg">Invoice #{sale.invoice_number}</p>
            </div>
            <div className="text-right">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{user?.business_name || user?.name || 'Watch Dealer'}</h2>
              <p className="text-gray-600">{user?.email}</p>
              <p className="text-gray-600">{user?.phone || 'Phone not set'}</p>
            </div>
          </div>

          <div className="h-1 bg-gradient-to-r from-slate-900 to-slate-700 rounded-full mb-8"></div>

          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Bill To</h3>
              <div className="text-gray-900">
                <p className="font-bold text-xl mb-1">{sale.client_name || 'Walk-in Customer'}</p>
                {sale.client_company && <p className="text-gray-600 mb-1">{sale.client_company}</p>}
                {sale.client_address && <p className="text-sm text-gray-600">{sale.client_address}</p>}
                {(sale.client_city || sale.client_state || sale.client_postal_code) && (
                  <p className="text-sm text-gray-600">
                    {sale.client_city}{sale.client_city && (sale.client_state || sale.client_postal_code) ? ', ' : ''}
                    {sale.client_state} {sale.client_postal_code}
                  </p>
                )}
                {sale.client_country && <p className="text-sm text-gray-600">{sale.client_country}</p>}
                {sale.client_email && <p className="text-sm text-gray-600 mt-2">{sale.client_email}</p>}
                {sale.client_phone && <p className="text-sm text-gray-600">{sale.client_phone}</p>}
              </div>
            </div>
            <div className="text-right">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Invoice Details</h3>
              <div className="space-y-1 text-gray-900">
                <p><span className="text-gray-600">Date:</span> <span className="font-semibold">{formatDate(sale.sale_date || sale.created_at)}</span></p>
                <p><span className="text-gray-600">Payment Method:</span> <span className="font-semibold">{sale.payment_method || 'N/A'}</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Items Table */}
        <div className="mb-12">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-4 text-sm font-bold text-gray-600 uppercase tracking-wider">Item Description</th>
                <th className="text-right py-4 text-sm font-bold text-gray-600 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-6">
                  <div className="font-bold text-lg text-gray-900 mb-2">
                    {sale.watch_brand} {sale.watch_model}
                  </div>
                  <div className="mb-2 flex gap-2 flex-wrap">
                    {sale.watch_reference && (
                      <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold">
                        REF: {sale.watch_reference}
                      </span>
                    )}
                    {sale.watch_serial_number && (
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold">
                        S/N: {sale.watch_serial_number}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>• Luxury Swiss timepiece</p>
                    <p>• Authenticated and verified genuine</p>
                    <p>• Complete with documentation</p>
                  </div>
                </td>
                <td className="py-6 text-right align-top">
                  <div className="text-2xl font-bold text-gray-900">
                    ${(Number(sale.sale_price) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Invoice Totals */}
        <div className="flex justify-end mb-12">
          <div className="w-96">
            <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
              <div className="flex justify-between items-center pb-4 border-b-2 border-gray-200">
                <span className="text-xl font-bold text-gray-900">TOTAL</span>
                <span className="text-3xl font-bold text-gray-900">
                  ${(Number(sale.sale_price) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              {/* Hide profit info when printing - internal use only */}
              <div className="print:hidden">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Profit</span>
                  <span className={`font-bold ${parseFloat(sale.profit) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${(Number(sale.profit) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2">
                  <span className="text-gray-600">Profit Margin</span>
                  <span className="font-bold text-slate-900">
                    {(Number(sale.profit_margin) || 0).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {sale.notes && (
          <div className="mb-8">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Additional Information</h3>
            <div className="text-gray-700 bg-gray-50 rounded-xl p-4 whitespace-pre-line">{sale.notes}</div>
          </div>
        )}

        {/* Item Description */}
        <div className="mb-8 p-6 bg-gray-50 border-2 border-slate-100 rounded-2xl">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Item Details</h3>
          <div className="text-sm text-gray-700 space-y-2">
            <p>• Please inspect the item upon receipt</p>
            <p>• Original parts and papers (where applicable)</p>
            <p>• Warranty information (if applicable) provided separately</p>
            <p>• Contact us with any questions or concerns</p>
          </div>
        </div>

        {/* Payment Instructions - Show wire/zelle details if available */}
        {invoice && (invoice.payment_method === 'Wire' || invoice.payment_method === 'Zelle') && (
          <div className="mb-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Instructions</h3>

            {invoice.payment_method === 'Wire' && (
              <div className="space-y-2 text-sm text-gray-800">
                <p className="font-semibold text-gray-900">Wire Transfer Details:</p>
                {invoice.account_name && (
                  <p><span className="font-medium">Account Name:</span> {invoice.account_name}</p>
                )}
                {invoice.account_number && (
                  <p><span className="font-medium">Account Number:</span> {invoice.account_number}</p>
                )}
                {invoice.routing_number && (
                  <p><span className="font-medium">Routing Number:</span> {invoice.routing_number}</p>
                )}
                {invoice.account_address && (
                  <p><span className="font-medium">Bank Address:</span> {invoice.account_address}</p>
                )}
                <p className="text-xs text-gray-600 mt-3">
                  Please reference Invoice #{sale.invoice_number} when sending payment
                </p>
              </div>
            )}

            {invoice.payment_method === 'Zelle' && invoice.zelle_email && (
              <div className="space-y-2 text-sm text-gray-800">
                <p className="font-semibold text-gray-900">Send payment via Zelle to:</p>
                <p className="text-lg font-bold text-blue-600">{invoice.zelle_email}</p>
                <p className="text-xs text-gray-600 mt-3">
                  Please reference Invoice #{sale.invoice_number} when sending payment
                </p>
              </div>
            )}
          </div>
        )}

        {/* Important Notice */}
        <div className="mb-8">
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Important Notice</h3>
          <div className="text-xs text-gray-600 space-y-1">
            <p>• Please review the sale terms and conditions provided at time of purchase</p>
            <p>• Any warranty coverage is provided separately and subject to terms</p>
            <p>• Regular servicing recommended every 3-5 years for mechanical watches</p>
            <p>• Keep all documentation in a safe place for insurance and resale purposes</p>
          </div>
        </div>

        {/* Invoice Footer */}
        <div className="border-t-2 border-gray-100 pt-8">
          <div className="text-center text-gray-500 text-sm">
            <p className="mb-1">Thank you for your business!</p>
            <p>This invoice was generated automatically on {formatDate(sale.created_at)}</p>
            <p className="mt-4 text-xs">For any questions regarding this invoice, please contact us at {user?.email}</p>
          </div>
        </div>
      </div>

      {/* Internal Analytics - Hidden when printing */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100 print:hidden">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Internal Analytics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border-2 border-emerald-100">
            <div className="text-sm font-semibold text-gray-600 mb-2">Sale Price</div>
            <div className="text-3xl font-bold text-gray-900">
              ${(Number(sale.sale_price) || 0).toLocaleString()}
            </div>
          </div>
          <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-100">
            <div className="text-sm font-semibold text-gray-600 mb-2">Profit</div>
            <div className={`text-3xl font-bold ${parseFloat(sale.profit) > 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${(Number(sale.profit) || 0).toLocaleString()}
            </div>
          </div>
          <div className="p-6 bg-gradient-to-br from-slate-50 to-stone-50 rounded-2xl border-2 border-slate-100">
            <div className="text-sm font-semibold text-gray-600 mb-2">Margin</div>
            <div className="text-3xl font-bold text-slate-900">
              {(Number(sale.profit_margin) || 0).toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Consignment Payout - Only show if this is a consignment watch */}
        {sale.is_consignment && (
          <div className="mt-6">
            <div className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200">
              <h4 className="text-xl font-bold text-amber-900 mb-4 flex items-center gap-2">
                <span>⚠️</span> Consignment Watch
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm font-semibold text-gray-600 mb-1">Owner</div>
                  <div className="text-lg font-bold text-gray-900">
                    {sale.consignment_owner_name || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-600 mb-1">Commission Rate</div>
                  <div className="text-2xl font-bold text-amber-600">
                    {sale.commission_percentage != null ? `${(Number(sale.commission_percentage) || 0).toFixed(1)}%` : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-600 mb-1">Commission Amount</div>
                  <div className="text-2xl font-bold text-green-600">
                    ${sale.commission_amount != null ? (Number(sale.commission_amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-600 mb-1">Owner Payout</div>
                  <div className="text-2xl font-bold text-blue-600">
                    ${sale.owner_payout != null ? (Number(sale.owner_payout) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-amber-100 rounded-lg border border-amber-300">
                <p className="text-sm text-amber-900">
                  <strong>Reminder:</strong> Owner payout of ${sale.owner_payout != null ? (Number(sale.owner_payout) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'} needs to be sent to {sale.consignment_owner_name || 'the owner'}.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print-specific styling */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0.5in;
          }

          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            margin: 0;
            padding: 0;
          }

          /* Hide everything except the invoice */
          body > *:not(.space-y-6) {
            display: none !important;
          }

          .print\\:hidden {
            display: none !important;
          }

          .print\\:shadow-none {
            box-shadow: none !important;
          }

          .print\\:border-0 {
            border: 0 !important;
          }

          /* Remove margins and padding from the container */
          .space-y-6 {
            padding: 0 !important;
            margin: 0 !important;
          }

          /* Ensure invoice takes full page */
          .bg-white {
            border-radius: 0 !important;
            padding: 0.5in !important;
            margin: 0 !important;
          }

          /* Prevent page breaks inside important sections */
          .mb-12, .mb-8 {
            page-break-inside: avoid;
          }

          /* Ensure colors print correctly */
          .bg-gradient-to-r {
            background: #4f46e5 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SaleDetailPage;
